import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
// 1. IMPORT getTaskStatus
import { addTextToImage, getTaskStatus } from '../../services/chatApi';
import { Download, Upload, Type, Plus, Trash2, Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { dataURLtoFile } from '../../utils/imageUtils';
import ToolNavigator from '../../components/ToolNavigator';
import useImageTransfer from '../../hooks/useImageTransfer';

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €";

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const AddTextToImage = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    const location = useLocation();
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [textElements, setTextElements] = useState([]);
    const [processedImage, setProcessedImage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // 2. ADD pollInterval Ref
    const pollInterval = useRef(null);

    const imageRef = useRef(null);
    const [imgDimensions, setImgDimensions] = useState({ width: 0, height: 0, displayWidth: 0, displayHeight: 0 });

    const availableFonts = [
        { name: 'Arial', value: 'arial.ttf' },
        { name: 'Times New Roman', value: 'times.ttf' },
        { name: 'Fluent Light', value: 'Fluent-ExtraLight.ttf' }
    ];

    const resetParams = useCallback(() => {
        setProcessedImage(null);
        setError(null);
        setIsLoading(false);
        if (pollInterval.current) clearInterval(pollInterval.current);
    }, []);

    // 2. FIX: Handle the logic when a file is received (either via upload OR transfer)
    useEffect(() => {
        if (file && textElements.length === 0) {
            setTextElements([{ 
                id: Date.now(), 
                text: t('addTextToImage.upload.queryPlaceholderText'), 
                x: 50, 
                y: 50, 
                size: 40, 
                color: "#ffffff", 
                font: "arial.ttf",
                displayPos: { x: 50, y: 50 }
            }]);
        }
    }, [file, textElements.length, t]);

    // 3. ACTIVATE HOOK: Using the same pattern as ConvertImage
    useImageTransfer(setFile, setPreviewUrl, setProcessedImage, resetParams);

    useEffect(() => {
        return () => clearInterval(pollInterval.current);
    }, []);

    const resetTool = useCallback(() => {
        setFile(null);
        setPreviewUrl(null);
        setProcessedImage(null);
        setTextElements([]);
        setError(null);
        clearInterval(pollInterval.current);
    }, []);

    // Handle incoming image transfers
    useImageTransfer(setFile, setPreviewUrl, setProcessedImage, resetTool);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setPreviewUrl(URL.createObjectURL(selectedFile));
            setProcessedImage(null);
            setError(null);
        }
    };

    const onImgLoad = (e) => {
        setImgDimensions({
            width: e.target.naturalWidth,
            height: e.target.naturalHeight,
            displayWidth: e.target.clientWidth,
            displayHeight: e.target.clientHeight
        });
    };

    const handleImageClick = (e) => {
        if (!imageRef.current || processedImage) return;
        
        const rect = imageRef.current.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;

        const activeId = textElements.length > 0 ? textElements[textElements.length - 1].id : null;
        
        if (activeId) {
            updateElement(activeId, 'displayPos', { x: clickX, y: clickY });
        }
    };

    const addTextElement = () => {
        if (textElements.length >= 5) return;
        setTextElements([...textElements, {
            id: Date.now(),
            text: t('addTextToImage.upload.queryPlaceholderNewText'),
            x: 50,
            y: 50,
            displayPos: { x: 50, y: 50 },
            size: 40,
            color: "#ffffff",
            font: "arial.ttf"
        }]);
    };

    const removeElement = (id) => {
        setTextElements(textElements.filter(el => el.id !== id));
    };

    const updateElement = (id, field, value) => {
        setTextElements(textElements.map(el => {
            if (el.id === id) {
                if (field === 'displayPos') {
                    // Recalculate dimensions on click to ensure accuracy even if window resized
                    const currentImg = imageRef.current;
                    const scaleX = currentImg ? currentImg.naturalWidth / currentImg.clientWidth : 1;
                    const scaleY = currentImg ? currentImg.naturalHeight / currentImg.clientHeight : 1;
                    
                    return { 
                        ...el, 
                        displayPos: value, 
                        x: Math.round(value.x * scaleX), 
                        y: Math.round(value.y * scaleY)  
                    };
                }
                return { ...el, [field]: value };
            }
            return el;
        }));
    };

    // 4. UPDATED ASYNC PROCESS HANDLER
    const handleProcess = async () => {
        if (!file || textElements.length === 0) return;
        
        setIsLoading(true);
        setError(null);
        
        try {
            // Prepare payload
            const elementsPayload = textElements.map(el => ({
                text: el.text,
                x: el.x !== undefined ? el.x : 50, // Fix 0 || 50 bug
                y: el.y !== undefined ? el.y : 50,
                size: el.size,
                color: el.color,
                font: el.font
            }));

            // Start Task
            const startResponse = await addTextToImage(file, elementsPayload);
            const taskId = startResponse.task_id; // Check for task_id

            if (!taskId) throw new Error("No Task ID returned");

            // Poll for Completion
            pollInterval.current = setInterval(async () => {
                try {
                    const statusRes = await getTaskStatus(taskId);
                    const status = statusRes.status || statusRes.state;

                    if (status === 'SUCCESS' || status === 'completed') {
                        clearInterval(pollInterval.current);
                        
                        const result = statusRes.result || statusRes.info;
                        if (result.new_credits !== undefined) {
                            setCredits(result.new_credits);
                        }
                        // Handle the URL returned by celery
                        if (result.download_url) {
                            setProcessedImage(result.download_url);
                            setIsLoading(false);
                        } else {
                            setError("Invalid response format from server.");
                            setIsLoading(false);
                        }

                    } else if (status === 'FAILURE' || status === 'failed') {
                        clearInterval(pollInterval.current);
                        setIsLoading(false);
                        setError(t('addTextToImage.errors.generic'));
                    }
                } catch (pollErr) {
                    clearInterval(pollInterval.current);
                    setIsLoading(false);
                    setError("Network error while checking status.");
                }
            }, 2000);

        } catch (err) {
            setError(err.message || t('addTextToImage.errors.generic'));
            setIsLoading(false);
        }
    };

    const handleDownload = async () => {
        if (!processedImage) return;
        try {
            const response = await fetch(processedImage);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            
            const link = document.createElement('a');
            link.href = url;
            link.download = "text_added_image.png";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (e) {
            window.open(processedImage, '_blank');
        }
    };

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? (
                <SubscribePrompt />
            ) : (
            <div className="max-w-6xl mx-auto">
                <h3 className="text-3xl font-bold text-brand-orange mb-2">
                    {t('addTextToImage.title')}
                </h3>
                <p className="text-slate-600 mb-6">
                    {t('addTextToImage.description')}
                </p>

                {!file ? (
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-12 text-center hover:border-brand-orange transition-colors bg-slate-50">
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="hidden"
                            id="image-upload"
                        />
                        <label htmlFor="image-upload" className="cursor-pointer flex flex-col items-center">
                            <div className="bg-white p-4 rounded-full shadow-sm mb-4">
                                <Upload size={32} className="text-brand-orange" />
                            </div>
                            <span className="text-lg font-medium text-slate-700">{t('addTextToImage.upload.label')}</span>
                            <span className="text-sm text-slate-500 mt-2">{t('addTextToImage.upload.hint')}</span>
                        </label>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        
                        {/* Left Column: Image Canvas */}
                        <div className="lg:col-span-7">
                            <div className="bg-slate-100 p-4 rounded-lg border border-slate-200">
                                <h4 className="font-semibold mb-2 flex items-center gap-2 text-brand-orange">
                                    <Type size={18} /> 
                                    {processedImage ? t('addTextToImage.canvas.titleResult') : t('addTextToImage.canvas.titleInteractive')}
                                </h4>
                                
                                <div className="relative overflow-hidden inline-block w-full min-h-[300px] flex items-center justify-center">
                                    {processedImage ? (
                                        <img src={processedImage} alt="Result" className="w-full h-auto rounded shadow-sm" />
                                    ) : (
                                        <div className="relative cursor-crosshair">
                                            <img 
                                                ref={imageRef}
                                                src={previewUrl} 
                                                alt="Original" 
                                                className={`w-full h-auto rounded transition-opacity ${isLoading ? 'opacity-50' : 'opacity-100'}`}
                                                onLoad={onImgLoad}
                                                onClick={handleImageClick}
                                            />
                                            
                                            {/* Loading Overlay */}
                                            {isLoading && (
                                                <div className="absolute inset-0 flex items-center justify-center z-20">
                                                    <Loader2 className="animate-spin text-brand-orange w-12 h-12" />
                                                </div>
                                            )}

                                            {!isLoading && textElements.map((el, idx) => (
                                                <div 
                                                    key={el.id}
                                                    className="absolute w-6 h-6 bg-brand-orange rounded-full border-2 border-white shadow-sm flex items-center justify-center text-white text-xs font-bold pointer-events-none transform -translate-x-1/2 -translate-y-1/2 z-10"
                                                    style={{ 
                                                        left: el.displayPos ? el.displayPos.x : '50%', 
                                                        top: el.displayPos ? el.displayPos.y : '50%' 
                                                    }}
                                                >
                                                    {idx + 1}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            <div className="mt-6 flex flex-col md:flex-row gap-4">
                                {processedImage ? (
                                    <>
                                        <button 
                                            onClick={handleDownload}
                                            className="flex-1 bg-green-600 text-white py-3 px-6 rounded-md hover:bg-green-700 transition flex items-center justify-center gap-2 font-semibold"
                                        >
                                            <Download size={20} /> {t('addTextToImage.buttons.download')}
                                        </button>
                                        <button 
                                            onClick={() => setProcessedImage(null)}
                                            className="bg-slate-200 text-slate-700 py-3 px-6 rounded-md hover:bg-slate-300 transition flex items-center justify-center gap-2"
                                        >
                                            <Type size={20} /> {t('addTextToImage.buttons.edit')}
                                        </button>
                                        <button 
                                            onClick={resetTool}
                                            className="bg-slate-100 text-slate-700 py-3 px-6 rounded-md hover:bg-slate-200 transition flex items-center justify-center gap-2"
                                        >
                                            <RefreshCw size={20} /> {t('addTextToImage.buttons.newImage')}
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button 
                                            onClick={handleProcess}
                                            disabled={isLoading}
                                            className="flex-1 bg-brand-orange text-white py-3 px-6 rounded-md hover:bg-orange-600 transition flex items-center justify-center gap-2 font-semibold disabled:opacity-70"
                                        >
                                            {isLoading ? <Loader2 className="animate-spin" /> : <Type />} 
                                            {isLoading ? t('addTextToImage.buttons.processing') : t('addTextToImage.buttons.process')}
                                        </button>
                                        <button 
                                            onClick={resetTool}
                                            disabled={isLoading}
                                            className="bg-slate-200 text-slate-700 py-3 px-6 rounded-md hover:bg-slate-300 transition disabled:opacity-50"
                                        >
                                            {t('addTextToImage.buttons.reset')}
                                        </button>
                                    </>
                                )}
                            </div>
                            
                            {error && (
                                <div className="mt-4 text-center p-3 text-red-600 bg-red-50 rounded border border-red-200">
                                    {error}
                                </div>
                            )}

                            {processedImage && (
                                <div className="mt-4">
                                    <ToolNavigator currentImage={processedImage} />
                                </div>
                            )}
                        </div>

                        {!processedImage && (
                            <div className={`lg:col-span-5 space-y-4 ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
                                <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                                    <div className="flex justify-between items-center mb-4">
                                        <h4 className="font-semibold text-xl text-brand-dark">{t('addTextToImage.controls.title')}</h4>
                                        <button 
                                            onClick={addTextElement}
                                            disabled={textElements.length >= 5}
                                            className="text-sm bg-blue-50 text-blue-600 px-3 py-2 rounded-md hover:bg-blue-100 disabled:opacity-50 font-medium flex items-center gap-1 transition"
                                        >
                                            <Plus size={16} /> {t('addTextToImage.controls.addText')}
                                        </button>
                                    </div>

                                    <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                                        {textElements.map((el, idx) => (
                                            <div key={el.id} className="bg-slate-50 p-4 rounded-lg border border-slate-200 relative group transition hover:border-brand-orange">
                                                <div className="absolute top-2 right-2 bg-slate-200 text-slate-500 w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold">
                                                    {idx + 1}
                                                </div>
                                                
                                                <div className="space-y-3">
                                                    <div>
                                                        <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">{t('addTextToImage.controls.labels.content')}</label>
                                                        <textarea 
                                                            value={el.text}
                                                            onChange={(e) => updateElement(el.id, 'text', e.target.value)}
                                                            className="w-full p-2 border border-slate-300 rounded focus:ring-2 focus:ring-brand-orange focus:outline-none text-sm"
                                                            rows="2"
                                                        />
                                                    </div>
                                                    
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <div>
                                                            <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">{t('addTextToImage.controls.labels.size')}</label>
                                                            <input 
                                                                type="number"
                                                                value={el.size}
                                                                onChange={(e) => updateElement(el.id, 'size', parseInt(e.target.value))}
                                                                className="w-full p-2 border border-slate-300 rounded focus:outline-none text-sm"
                                                                min="10" max="500"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">{t('addTextToImage.controls.labels.color')}</label>
                                                            <div className="flex items-center gap-2">
                                                                <input 
                                                                    type="color"
                                                                    value={el.color}
                                                                    onChange={(e) => updateElement(el.id, 'color', e.target.value)}
                                                                    className="h-9 w-12 cursor-pointer border rounded"
                                                                />
                                                                <span className="text-xs text-slate-500 font-mono">{el.color}</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-3">
                                                        <div className="col-span-2">
                                                            <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">{t('addTextToImage.controls.labels.font')}</label>
                                                            <select 
                                                                value={el.font}
                                                                onChange={(e) => updateElement(el.id, 'font', e.target.value)}
                                                                className="w-full p-2 border border-slate-300 rounded focus:outline-none text-sm"
                                                            >
                                                                {availableFonts.map(f => (
                                                                    <option key={f.value} value={f.value}>{f.name}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="pt-2 border-t border-slate-200 flex justify-between items-center mt-2">
                                                        <p className="text-xs text-slate-400 italic">
                                                            {t('addTextToImage.controls.labels.positionHint')}
                                                        </p>
                                                        {textElements.length > 1 && (
                                                            <button 
                                                                onClick={() => removeElement(el.id)}
                                                                className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded transition"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            )}
        </div>
    );
};

export default AddTextToImage;
